/* global document, window, fetch, URL, console */
// Pure functions first (no DOM), so this stays unit-testable under this
// repo's `environment: 'node'` vitest config — same split as
// somm-johns-island-club-schedule.js's clubsFromDatasets/readClubsFromDom.
// The DOM-mounting block at the bottom is the only browser-only part.

import { expandRecurringEvents } from './somm-recurring-events.js';

export function nyTodayISO(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type) => parts.find((p) => p.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function formatTime12h(hhmm) {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

export function formatDateShort(iso) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${iso}T12:00:00Z`)
  );
}

export function dedupeKey(title, dateIso) {
  return `${(title || '').trim().toLowerCase()}|${dateIso}`;
}

// Hosts this store's own domains live on. A feed `url` pointing at one of
// these — or carrying a `?view=` param, the retiring alternate-template
// shim mechanism (spec decision #5: no `?view=` shims for Nexton) — is
// never a real reservation link. It's either a stale link to a page this
// theme no longer serves that way, or a same-site page with nothing to
// reserve (e.g. a walk-in event's own events-page link). There is no
// "walk-in category" — this URL check is the entire CTA rule (contract
// EVENTS-FEED-CONTRACT.md §2). `myshopify.com` is generic (not just
// sommnxt's own subdomain) so it also covers other stores' preview/admin
// hosts on this shared theme.
const INTERNAL_HOSTS = ['sommwine.co', 'sommchs.com', 'myshopify.com'];

export function isExternalReservationUrl(url) {
  if (!url) return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.searchParams.has('view')) return false;
  const host = parsed.hostname.toLowerCase();
  const isInternal = INTERNAL_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  return !isInternal;
}

// A title-matched `wine_club_override` block always wins, regardless of the
// feed record's own `category` — the feed's category taxonomy is staff data
// entry and not reliably set to "wine-club" for every pickup-party record.
// Wine-club pickup parties never trust the feed's own `url` either way (it's
// often a stale `?view=` shim); only the override's product link may supply
// that CTA. Everything else gets a Reserve CTA only when its own url is a
// genuinely external reservation link (see isExternalReservationUrl above).
export function computeFeedCta(record, overrides = []) {
  const title = (record.title || '').trim().toLowerCase();
  const match = overrides.find(
    (o) => o.href && title.includes((o.matchTitle || '').trim().toLowerCase())
  );
  if (match) return { label: 'Join the club', href: match.href, external: false };
  if (isExternalReservationUrl(record.url)) return { label: 'Reserve', href: record.url, external: true };
  return null;
}

export function parseFeed(json) {
  if (!json || json.schemaVersion !== 2 || !Array.isArray(json.events)) return [];
  return json.events;
}

// Matches the theme setting's exact-title list (one line per title,
// case-insensitive, trimmed) — distinct from computeFeedCta's substring
// `matchTitle` override, which is deliberately loose for club pickup
// parties. Featuring a feed event is an exact, unambiguous pick.
export function isRecordFeatured(title, featuredTitles = []) {
  const normalized = (title || '').trim().toLowerCase();
  return featuredTitles.some((candidate) => (candidate || '').trim().toLowerCase() === normalized);
}

// Expands every non-managedPaid record into its occurrences (via
// expandRecurringEvents — the one recurrence engine, shared with the pure
// somm-recurring-events.js module), drops anything that collides (by
// title+date) with an already-rendered ticket card — the product-sourced
// card always wins that collision, since it carries real
// availability/price/CTA and the feed record doesn't; that title+date
// fallback is unchanged from N4 — and returns the survivors sorted
// chronologically. Occurrence identity for feed-internal dedupe/refresh is
// the occurrence's own `id` (`${ruleId}-${date}`, from expandRecurringEvents),
// not title+date.
export function buildFeedItems(
  records,
  { now = new Date(), overrides = [], ticketKeys = new Set(), featuredTitles = [], horizonDays = 90 } = {}
) {
  // managedPaid records are paid-card projections — never render them as
  // free cards until paid-card consumption exists (contract §2, §5b).
  const activeRecords = (records || []).filter((record) => record?.managedPaid !== true);
  const occurrences = expandRecurringEvents(activeRecords, now, horizonDays);
  const items = [];
  const seenOccurrenceIds = new Set();
  for (const occurrence of occurrences) {
    if (seenOccurrenceIds.has(occurrence.id)) continue;
    seenOccurrenceIds.add(occurrence.id);
    const dateIso = occurrence.date;
    const key = dedupeKey(occurrence.title, dateIso);
    if (ticketKeys.has(key)) continue;
    items.push({
      key,
      occurrenceId: occurrence.id,
      dateIso,
      startTime: occurrence.startTime,
      endTime: occurrence.endTime,
      title: occurrence.title,
      description: occurrence.description,
      imageUrl: occurrence.imageUrl,
      imageAlt: occurrence.imageAlt,
      cta: computeFeedCta(occurrence, overrides),
      featured: isRecordFeatured(occurrence.title, featuredTitles),
    });
  }
  return items.sort((a, b) => {
    const ak = `${a.dateIso}T${a.startTime || ''}`;
    const bk = `${b.dateIso}T${b.startTime || ''}`;
    return ak < bk ? -1 : ak > bk ? 1 : 0;
  });
}

// Featured items are pinned to the front only where the list has a real cap
// (count_limit > 0, e.g. the homepage teaser) — an uncapped page keeps pure
// chronological order and relies on the Featured label on the item itself,
// per Ryan's direction that a full events page shouldn't reorder.
export function orderEvents(items, { countLimit = 0 } = {}) {
  if (countLimit > 0) {
    return [...items].sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0;
    });
  }
  return [...items].sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
}

export function applyCountLimit(orderedItems, countLimit) {
  return orderedItems.map((item, index) => ({
    ...item,
    hidden: countLimit > 0 && index + 1 > countLimit,
  }));
}

function buildFeedRow(item) {
  const li = document.createElement('li');
  li.className = 'somm-events-worker__row';
  li.dataset.sortKey = `${item.dateIso}T${item.startTime || ''}`;
  // Marks this row as feed-sourced (as opposed to a server-rendered ticket
  // row, which carries data-somm-event-item instead) so a re-render
  // (shopify:section:load) can remove stale feed rows before rebuilding —
  // expansion can now produce many rows per section, so a missed cleanup
  // would duplicate every occurrence of every series on re-render.
  li.dataset.sommFeedItem = 'true';

  const dateEl = document.createElement('p');
  dateEl.className = 'somm-events-worker__date';
  const time = document.createElement('time');
  time.dateTime = item.dateIso;
  time.textContent = formatDateShort(item.dateIso);
  const range = document.createElement('span');
  range.textContent = item.endTime
    ? `${formatTime12h(item.startTime)}–${formatTime12h(item.endTime)}`
    : formatTime12h(item.startTime);
  dateEl.append(time, range);

  const content = document.createElement('div');
  content.className = 'somm-events-worker__content';
  const h3 = document.createElement('h3');
  h3.textContent = item.title;
  content.append(h3);
  if (item.featured) {
    const badge = document.createElement('p');
    badge.className = 'somm-events-worker__badge';
    badge.textContent = 'Featured';
    content.append(badge);
  }
  if (item.description) {
    const details = document.createElement('details');
    details.className = 'somm-ji-event-details';
    const summary = document.createElement('summary');
    summary.textContent = 'Event details';
    const plus = document.createElement('span');
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';
    summary.append(plus);
    const body = document.createElement('div');
    body.textContent = item.description;
    details.append(summary, body);
    content.append(details);
  }

  const actions = document.createElement('div');
  actions.className = 'somm-events-worker__actions';
  if (item.cta) {
    const a = document.createElement('a');
    a.className = 'text-link somm-events-worker__link';
    a.href = item.cta.href;
    a.textContent = item.cta.label;
    if (item.cta.external) {
      a.target = '_blank';
      a.rel = 'noopener';
    }
    actions.append(a);
  }

  li.append(dateEl, content, actions);
  return li;
}

// Contract §1: page from 0, following `nextPage` until `complete: true`,
// capped at 4 fetches. Today's feed is a single page (nextPage null,
// complete true) — that's the normal case and this loop exits on the first
// iteration. If the cap is hit before `complete: true`, render whatever was
// fetched (never a blank section) but warn rather than truncate silently.
const FEED_PAGE_CAP = 4;

async function fetchFeedRecords(feedUrl) {
  const records = [];
  let page = 0;
  let reachedComplete = false;
  for (let fetches = 0; fetches < FEED_PAGE_CAP; fetches += 1) {
    const url = new URL(feedUrl);
    url.searchParams.set('page', String(page));
    const response = await fetch(url.href, { cache: 'no-store', headers: { accept: 'application/json' } });
    if (!response.ok) throw new Error('Feed request failed');
    const json = await response.json();
    records.push(...parseFeed(json));
    if (json?.complete === true) {
      reachedComplete = true;
      break;
    }
    if (typeof json?.nextPage !== 'number') break;
    page = json.nextPage;
  }
  if (!reachedComplete && typeof console !== 'undefined') {
    console.warn(
      '[somm-events-worker] feed did not report complete within the 4-page cap; rendering the events fetched so far'
    );
  }
  return records;
}

// N8b: a card, used for a feed item only when it lands in a featured slot on
// the capped homepage teaser (countLimit > 0) — mirrors the ticket-product
// image card (snippets/somm-event-ticket-card.liquid), but feed items have
// no product page or price, so there's no image link, no price line, and
// the CTA (if any) is the only action, exactly as buildFeedRow already
// computes it. Uncapped pages (the /pages/events chronological list) never
// call this — they keep the plain row for every feed item, unchanged.
//
// N8b round 2/3: no blank image slot, ever. Round 2's logo-on-navy
// placeholder didn't work (the bundled logo was navy-on-navy, invisible).
// Replaced: when there's no imageUrl, falls back to the section's
// default_event_image setting (rendered exactly like a real image, no
// crop); when that's unset too, a typographic card (title + date/time,
// cream on navy, no logo) — matching snippets/somm-event-ticket-card.liquid
// exactly. No "Featured" pill on a card, ticket or feed — the card
// treatment already signals it; the pill now lives only on buildFeedRow's
// plain rows.
function buildFeedCard(item, defaultEventImageUrl) {
  const li = document.createElement('li');
  li.className = 'somm-events-worker__row somm-events-worker__row--card';
  li.dataset.sortKey = `${item.dateIso}T${item.startTime || ''}`;
  li.dataset.sommFeedItem = 'true';

  const imageWrap = document.createElement('span');
  imageWrap.className = 'somm-events-worker__card-image-link';
  const resolvedImageUrl = item.imageUrl || defaultEventImageUrl;
  if (resolvedImageUrl) {
    const img = document.createElement('img');
    img.className = 'somm-events-worker__card-image';
    img.src = resolvedImageUrl;
    img.alt = item.imageUrl ? item.imageAlt || '' : '';
    img.loading = 'lazy';
    imageWrap.append(img);
  } else {
    const placeholder = document.createElement('div');
    placeholder.className = 'somm-events-worker__card-image--placeholder';
    const placeholderTitle = document.createElement('p');
    placeholderTitle.className = 'somm-events-worker__card-placeholder-title';
    placeholderTitle.textContent = item.title;
    placeholder.append(placeholderTitle);
    const placeholderDate = document.createElement('p');
    placeholderDate.className = 'somm-events-worker__card-placeholder-date';
    const placeholderTime = document.createElement('time');
    placeholderTime.dateTime = item.dateIso;
    placeholderTime.textContent = formatDateShort(item.dateIso);
    const placeholderRange = document.createElement('span');
    placeholderRange.textContent = item.endTime
      ? `${formatTime12h(item.startTime)}–${formatTime12h(item.endTime)}`
      : formatTime12h(item.startTime);
    placeholderDate.append(placeholderTime, placeholderRange);
    placeholder.append(placeholderDate);
    imageWrap.append(placeholder);
  }

  const content = document.createElement('div');
  content.className = 'somm-events-worker__card-content';

  const dateEl = document.createElement('p');
  dateEl.className = 'somm-events-worker__card-date';
  const time = document.createElement('time');
  time.dateTime = item.dateIso;
  time.textContent = formatDateShort(item.dateIso);
  const range = document.createElement('span');
  range.textContent = item.endTime
    ? `${formatTime12h(item.startTime)}–${formatTime12h(item.endTime)}`
    : formatTime12h(item.startTime);
  dateEl.append(time, range);
  content.append(dateEl);

  const h4 = document.createElement('h4');
  h4.className = 'somm-events-worker__card-title';
  h4.textContent = item.title;
  content.append(h4);

  if (item.cta) {
    const a = document.createElement('a');
    a.className = 'button button-red somm-events-worker__card-cta';
    a.href = item.cta.href;
    a.textContent = item.cta.label;
    if (item.cta.external) {
      a.target = '_blank';
      a.rel = 'noopener';
    }
    content.append(a);
  }

  li.append(imageWrap, content);
  return li;
}

async function enhance(section) {
  const list = section.querySelector('[data-somm-events-list]');
  const emptyEl = section.querySelector('[data-somm-events-empty]');
  if (!list) return;

  const countLimit = Number(section.dataset.countLimit) || 0;
  const feedUrl = section.dataset.feedUrl;
  const defaultEventImageUrl = section.dataset.sommDefaultEventImage || '';

  // Remove any feed-sourced rows from a previous enhance() pass before
  // rebuilding — ticket rows are left alone (they're existing DOM nodes,
  // re-appending just reorders them; only freshly built feed rows can
  // duplicate on re-render).
  list.querySelectorAll('[data-somm-feed-item]').forEach((el) => el.remove());

  const ticketItems = Array.from(list.querySelectorAll('[data-somm-event-item]')).map((el) => ({
    el,
    sortKey: el.dataset.sortKey,
    key: el.dataset.dedupeKey,
    featured: el.dataset.featured === 'true',
  }));
  const ticketKeys = new Set(ticketItems.map((t) => t.key));

  let feedNodes = [];
  if (feedUrl) {
    try {
      const records = await fetchFeedRecords(feedUrl);
      const overridesEl = section.querySelector('[data-somm-club-overrides]');
      const overrides = overridesEl ? JSON.parse(overridesEl.textContent || '[]') : [];
      const featuredTitlesEl = section.querySelector('[data-somm-featured-titles]');
      const featuredTitles = featuredTitlesEl ? JSON.parse(featuredTitlesEl.textContent || '[]') : [];
      const items = buildFeedItems(records, { now: new Date(), overrides, ticketKeys, featuredTitles });
      // N8b: featured slots get card treatment only on the capped homepage
      // teaser — the uncapped events page keeps every feed item as a plain
      // row, same as the ticket-product loop in the Liquid section.
      feedNodes = items.map((item) => ({
        el: countLimit > 0 && item.featured ? buildFeedCard(item, defaultEventImageUrl) : buildFeedRow(item),
        sortKey: `${item.dateIso}T${item.startTime || ''}`,
        featured: item.featured,
      }));
    } catch {
      // Fetch or shape failure: degrade to the already-rendered ticket
      // cards — identical to the no-JS state, so there's no separate
      // failure path to maintain.
      feedNodes = [];
    }
  }

  const ordered = orderEvents([...ticketItems, ...feedNodes], { countLimit });
  const withVisibility = applyCountLimit(ordered, countLimit);
  for (const { el, hidden } of withVisibility) {
    list.appendChild(el);
    el.hidden = hidden;
  }
  if (emptyEl) emptyEl.hidden = ordered.length > 0;
}

if (typeof document !== 'undefined') {
  const mount = () => document.querySelectorAll('[data-somm-events-worker]').forEach((section) => enhance(section));
  mount();
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.('[data-somm-events-worker]');
    if (section) enhance(section);
  });
}
